import { isEqual } from 'lodash'
import { FragmentType, useFragment } from '@/gql'
import { FieldableFormCustomFieldValueFragment } from '@/components/Fieldable/graphql/fragments'
import { CustomFieldType, CustomFieldTypeValueInput } from '@/gql/graphql'
import { CustomFieldTypeGqlToInput } from '@/components/Fields/utils/CustomFieldUtils'

export const mapCustomFieldValueToCustomFieldValueInput = (
  customFieldValues: FragmentType<typeof FieldableFormCustomFieldValueFragment>[],
): CustomFieldTypeValueInput[] => {
  const customFieldValuesTyped = useFragment(
    FieldableFormCustomFieldValueFragment,
    customFieldValues,
  )

  const multiSelectGroups = new Map<string, string[]>()
  const fileUploadGroups = new Map<string, string[]>()
  const result: CustomFieldTypeValueInput[] = []

  for (const customFieldValue of customFieldValuesTyped) {
    const { fieldType } = customFieldValue.customField

    if (fieldType === CustomFieldType.MultiSelect) {
      const fieldId = customFieldValue.customField.id
      if (!multiSelectGroups.has(fieldId)) {
        multiSelectGroups.set(fieldId, [])
      }
      if (customFieldValue.customFieldOption?.id) {
        multiSelectGroups.get(fieldId)!.push(customFieldValue.customFieldOption.id)
      }
    } else if (fieldType === CustomFieldType.FileUpload) {
      const fieldId = customFieldValue.customField.id
      if (!fileUploadGroups.has(fieldId)) {
        fileUploadGroups.set(fieldId, [])
      }
      if (customFieldValue.file?.id) {
        fileUploadGroups.get(fieldId)!.push(customFieldValue.file.id)
      }
    } else {
      const fieldValue = {
        [CustomFieldType.SingleSelect]: customFieldValue.customFieldOption?.id,
        [CustomFieldType.FreeText]: customFieldValue.content,
        [CustomFieldType.Date]: customFieldValue.content,
        // Stored as '1'/'0' in content; the input takes a real boolean.
        [CustomFieldType.Boolean]: customFieldValue.content === null ? null : customFieldValue.content === '1',
      }

      result.push({
        [CustomFieldTypeGqlToInput[fieldType]]: {
          id: customFieldValue.customField.id,
          value: fieldValue[fieldType],
        },
      })
    }
  }

  // Add grouped MultiSelect entries
  for (const [fieldId, values] of multiSelectGroups) {
    result.push({
      multiSelect: { id: fieldId, values },
    })
  }

  // Add grouped FileUpload entries (one input per field, fileIds as a list)
  for (const [fieldId, fileIds] of fileUploadGroups) {
    result.push({
      fileUpload: { id: fieldId, fileIds },
    })
  }

  return result
}

export const customFieldValueInputFieldId = (fieldValue: CustomFieldTypeValueInput): string | undefined =>
  fieldValue.singleSelect?.id ??
  fieldValue.freeText?.id ??
  fieldValue.multiSelect?.id ??
  fieldValue.fileUpload?.id ??
  fieldValue.date?.id ??
  fieldValue.boolean?.id ??
  undefined

/**
 * Keep only the field values that no longer match what the form started with.
 *
 * The backend deletes a stored value when it receives that field with a null
 * value, so a form must never re-submit fields the user did not touch: if the
 * form state holds an empty value for a stored field (failed option lookup,
 * value still loading, a stray input event), echoing it back erases real data.
 * Fields without an initial entry are always kept — they are new input.
 */
export const diffCustomFieldValueInputs = (
  current: CustomFieldTypeValueInput[],
  initial: CustomFieldTypeValueInput[],
): CustomFieldTypeValueInput[] => {
  const initialByFieldId = new Map(
    initial.map((fieldValue) => [customFieldValueInputFieldId(fieldValue), fieldValue]),
  )

  return current.filter((fieldValue) => {
    const before = initialByFieldId.get(customFieldValueInputFieldId(fieldValue))

    return before === undefined || !isEqual(before, fieldValue)
  })
}

export function hasFieldValue(fieldValue?: CustomFieldTypeValueInput): boolean {
  if (!fieldValue) return false
  // FileUpload may arrive in either shape: the singular `fileId` (initial
  // mapping for legacy single-file values) or the plural `fileIds` (what the
  // multi-file editor emits). Either non-empty form counts as "has value".
  const { fileUpload } = fieldValue
  const hasFile =
    fileUpload?.fileId != null ||
    (Array.isArray(fileUpload?.fileIds) && fileUpload.fileIds.length > 0)
  return (
    (fieldValue.freeText?.value != null && fieldValue.freeText.value !== '') ||
    fieldValue.singleSelect?.value != null ||
    (fieldValue.multiSelect?.values != null && fieldValue.multiSelect.values.length > 0) ||
    (fieldValue.date?.value != null && fieldValue.date.value !== '') ||
    fieldValue.boolean?.value != null ||
    hasFile
  )
}
